import { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash, Minus, Plus, CreditCard } from "@phosphor-icons/react";

export default function Cart() {
  const { items, remove, updateQty, total } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [form, setForm] = useState({ customer_name: "", email: "", address: "" });

  const submit = async (e) => {
    e.preventDefault();
    setCheckingOut(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ item_id: i.item_id, item_type: i.type || "merch", quantity: i.quantity })),
        origin_url: window.location.origin,
        customer_email: form.email,
        customer_name: form.customer_name,
        address: form.address,
      });
      if (data.url) window.location.href = data.url;
      else toast.error("No checkout URL returned");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Checkout failed";
      toast.error(typeof detail === "string" ? detail : "Checkout failed");
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="cart-page">
      <div className="section-eyebrow">Checkout</div>
      <h1 className="font-display text-5xl lg:text-6xl uppercase mt-3">Your Cart</h1>

      {items.length === 0 ? (
        <div className="mt-12 scu-card p-12 text-center" data-testid="empty-cart">
          <p className="text-dim">Your cart is empty. The crown is waiting.</p>
          <Link to="/shop" className="btn-red inline-block mt-6">Shop Merch</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-8 mt-10">
          <div className="lg:col-span-7 space-y-4" data-testid="cart-items">
            {items.map((i) => (
              <div key={i.item_id} className="scu-card p-4 flex gap-4 items-center" data-testid={`cart-item-${i.item_id}`}>
                <img src={i.image} alt={i.name} className="w-24 h-24 object-cover" />
                <div className="flex-1">
                  <div className="font-display text-lg uppercase">{i.name}</div>
                  <div className="text-dim text-sm">${i.price.toFixed(2)}</div>
                  <div className="mt-2 inline-flex items-center border border-white/15">
                    <button onClick={() => updateQty(i.item_id, i.quantity - 1)} className="px-2 py-1 hover:bg-white/5" data-testid={`qty-dec-${i.item_id}`}><Minus size={14} /></button>
                    <span className="px-3 font-display" data-testid={`qty-${i.item_id}`}>{i.quantity}</span>
                    <button onClick={() => updateQty(i.item_id, i.quantity + 1)} className="px-2 py-1 hover:bg-white/5" data-testid={`qty-inc-${i.item_id}`}><Plus size={14} /></button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl">${(i.price * i.quantity).toFixed(2)}</div>
                  <button onClick={() => remove(i.item_id)} className="mt-2 text-dim hover:text-[#FF3B30] inline-flex items-center gap-1 text-xs uppercase tracking-widest" data-testid={`remove-${i.item_id}`}>
                    <Trash size={14} /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-5">
            <form onSubmit={submit} className="scu-card p-6 space-y-4" data-testid="checkout-form">
              <h3 className="font-display text-2xl uppercase">Checkout</h3>
              <p className="text-dim text-xs">Stripe test mode — use card <span className="text-white">4242 4242 4242 4242</span>, any future date, any CVC.</p>
              <input required placeholder="Full Name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                     className="w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none" data-testid="checkout-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none" data-testid="checkout-email" />
              <textarea required placeholder="Shipping Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                        className="w-full bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none min-h-[80px]" data-testid="checkout-address" />
              <div className="flex items-center justify-between pt-2 border-t border-white/10">
                <span className="text-dim uppercase tracking-widest text-xs">Total</span>
                <span className="font-display text-3xl text-[#FF3B30]" data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <button type="submit" disabled={checkingOut} className="btn-red w-full inline-flex items-center justify-center gap-2" data-testid="place-order-btn">
                <CreditCard size={18} weight="bold" /> {checkingOut ? "Redirecting..." : "Pay with Stripe"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
